/* The mock corpus. 15-api-contract.md section 6 is explicit that the
 * frontend builds against a fixture corpus generated from the contract
 * rather than hand-written dummy data, because a hand-written stub is an
 * undocumented second interpretation that drifts from the contract silently.
 * This is that corpus - generated once, deterministically (lib/rng.ts), from
 * a small set of named projects and the corridors they fly, so every
 * screen's numbers are internally consistent (a project's tree_count here is
 * always the count of the trees actually generated for its latest round,
 * never a separately hand-typed figure).
 *
 * Scale is deliberately far below the deck's own example figures (12,400
 * spans, 890 circuit miles) - those are a print-shot illustration, not a
 * target. This corpus is sized to prove pagination, filtering and the null/
 * empty cases, not to match a screenshot.
 *
 * 15 section 6.1 requires the corpus to carry the awkward cases from day
 * one - a wholly-unassessed page, an empty collection, circuit_id: null
 * trees, growth_rate_inferred: true - each is called out at the point it is
 * produced below, rather than bolted on afterward.
 *
 * Fields with no contract or schema source (work plan, notifications,
 * reports, cadence scenarios) are generated in their own section at the
 * bottom, separated because they are illustrative, not contract-backed.
 * 05-kanopy-verification.md section 8 says the economics and operations
 * screens (S06/S07/D05/D06) cannot be demoed from Kanopy data alone.
 * Clearance thresholds are not part of that set - 20-schema.md section 3.1a
 * documents the real clearance_threshold table this mirrors, and
 * 15-api-contract.md cites that section directly.
 */
import { createRng } from "../lib/rng";
import { feetToMeters, treeHandle } from "../lib/format";
import { riskStepForClearance, type BandShape } from "../lib/risk";
import type {
  AppNotification,
  Capture,
  CaptureProgressStage,
  CaptureRound,
  CadenceScenario,
  Circuit,
  ClearanceThreshold,
  ConditionBand,
  Coordinates,
  Pole,
  Prescription,
  Project,
  ReportDraft,
  RiskStep,
  Span,
  Tree,
  VolumeBand,
  WorkPacket,
  WorkPacketLineItem,
  WorkPlanPortfolio,
} from "../api/types";

const rng = createRng(0x41524252); // "ARBR" - fixed seed, stable across reloads

// ---------------------------------------------------------------------------
// 1. Corridors and projects
// ---------------------------------------------------------------------------

/* A corridor is what one capture flies - one Kanopy job (15 section 3.1). It
 * is a generation unit here and nothing more: no screen navigates by it. */
export interface CorridorDef {
  name: string;
  voltageClass: string;
  lengthMi: number;
  spanCount: number;
}

export const CORRIDOR_DEFS: CorridorDef[] = [
  { name: "Ridgeline 42", voltageClass: "12 kV", lengthMi: 38.2, spanCount: 120 },
  { name: "Marsh Creek 17", voltageClass: "12 kV", lengthMi: 31.6, spanCount: 100 },
  { name: "Fielding 08", voltageClass: "12 kV", lengthMi: 22.4, spanCount: 70 },
  { name: "Northgate 23", voltageClass: "25 kV", lengthMi: 19.8, spanCount: 60 },
  { name: "Halstead 05", voltageClass: "25 kV", lengthMi: 17.1, spanCount: 50 },
  { name: "Corley 31", voltageClass: "12 kV", lengthMi: 15.9, spanCount: 45 },
  { name: "Dunmore 14", voltageClass: "4 kV", lengthMi: 14.2, spanCount: 40 },
  { name: "West Bay 09", voltageClass: "4 kV", lengthMi: 12.8, spanCount: 35 },
  { name: "Ames Hollow 27", voltageClass: "4 kV", lengthMi: 11.4, spanCount: 30 },
];

// A rough grid of origins, one per corridor, so corridors don't overlap on
// the mock map - not real GIS, just enough separation to look distinct.
const ORIGINS = [
  { lat: 40.21, lon: -84.77 },
  { lat: 40.18, lon: -84.62 },
  { lat: 40.05, lon: -84.70 },
  { lat: 40.30, lon: -84.55 },
  { lat: 40.12, lon: -84.90 },
  { lat: 39.98, lon: -84.58 },
  { lat: 40.35, lon: -84.80 },
  { lat: 40.02, lon: -84.40 },
  { lat: 40.25, lon: -84.35 },
];

function uuid(prefix: string, i: number): string {
  return `${prefix}-${i.toString(36).padStart(6, "0")}`;
}

/* One capture round (20 section 3.4) - the flights one Kanopy project holds. */
interface RoundDef {
  name: string; // as Kanopy names the project this round mirrors
  capturedAt: string;
  archivedAt: string | null;
  accuracyPct: number;
  corridors?: string[]; // the corridors this round flew; omitted means all of the project's
}

/* S08's in-progress and failed rows - awkward on purpose, not tidy rows. They
 * land in their project's latest round, where a new capture always lands
 * (20 section 3.4). */
interface InFlightDef {
  corridor: string;
  status: "processing" | "failed";
  capturedAt: string | null; // null models the in-progress capture, S08
  // Set only on the processing capture - Captures.tsx reads these instead of
  // a fixed 68%, so a screen showing a different capture mid-flight isn't
  // stuck repeating this one's numbers (FOUNDATIONS 06: "progress is
  // honest").
  progressPct?: number;
  progressStage?: CaptureProgressStage;
  etaMinutes?: number;
}

/* Ours (20 section 3.2a). A project's corridors share one class, and its
 * latest round stays within MAX_LIMIT trees, because a project screen reads
 * that round as one page. Several rounds stand for a merged pair (15
 * section 4); the West Bay pair is one place filed as more than one project,
 * the Butler OH shape (05 section 4a). */
export interface ProjectDef {
  name: string;
  utilityName: string;
  description: string | null;
  corridors: string[];
  historicalCadenceYears: number; // synthetic - S07's "cadence by project group"
  rounds: RoundDef[]; // oldest first; the last is the latest
  inFlight?: InFlightDef[];
}

const UTILITY = "Ridgeline Electric";

export const PROJECT_DEFS: ProjectDef[] = [
  {
    name: "Ridgeline North",
    utilityName: UTILITY,
    description: "Northern distribution feeders.",
    corridors: ["Ridgeline 42"],
    historicalCadenceYears: 3,
    rounds: [
      { name: "Ridgeline North 2022", capturedAt: "2022-03-10T14:00:00Z", archivedAt: "2024-03-14T14:00:00Z", accuracyPct: 94 },
      { name: "Ridgeline North 2024", capturedAt: "2024-03-14T14:00:00Z", archivedAt: null, accuracyPct: 96 },
      { name: "Ridgeline North 2026", capturedAt: "2026-03-12T14:00:00Z", archivedAt: null, accuracyPct: 98 },
    ],
    inFlight: [
      { corridor: "Ridgeline 42", status: "processing", capturedAt: null, progressPct: 68, progressStage: "measurement", etaMinutes: 260 },
      { corridor: "Ridgeline 42", status: "failed", capturedAt: "2026-03-14T06:00:00Z" },
    ],
  },
  {
    name: "Marsh Creek",
    utilityName: UTILITY,
    description: null,
    corridors: ["Marsh Creek 17"],
    historicalCadenceYears: 3,
    rounds: [
      { name: "Marsh Creek 2022", capturedAt: "2022-03-10T14:00:00Z", archivedAt: "2024-03-14T14:00:00Z", accuracyPct: 94 },
      { name: "Marsh Creek 2024", capturedAt: "2024-03-14T14:00:00Z", archivedAt: null, accuracyPct: 96 },
      { name: "Marsh Creek 2025", capturedAt: "2025-11-08T14:00:00Z", archivedAt: null, accuracyPct: 91 },
      { name: "Marsh Creek 2026", capturedAt: "2026-03-12T14:00:00Z", archivedAt: null, accuracyPct: 98 },
    ],
  },
  {
    name: "Fielding–Corley",
    utilityName: UTILITY,
    description: "Two feeders flown together.",
    corridors: ["Fielding 08", "Corley 31"],
    historicalCadenceYears: 4,
    rounds: [
      { name: "Fielding–Corley 2022", capturedAt: "2022-03-10T14:00:00Z", archivedAt: "2024-03-14T14:00:00Z", accuracyPct: 94 },
      { name: "Fielding–Corley 2024", capturedAt: "2024-03-14T14:00:00Z", archivedAt: null, accuracyPct: 96 },
      // A round that flew only part of its project.
      { name: "Fielding 2025", capturedAt: "2025-11-08T14:00:00Z", archivedAt: null, accuracyPct: 91, corridors: ["Fielding 08"] },
      { name: "Fielding–Corley 2026", capturedAt: "2026-03-12T14:00:00Z", archivedAt: null, accuracyPct: 98 },
    ],
  },
  {
    name: "Northgate–Halstead",
    utilityName: UTILITY,
    description: null,
    corridors: ["Northgate 23", "Halstead 05"],
    historicalCadenceYears: 4,
    rounds: [
      { name: "Northgate–Halstead 2022", capturedAt: "2022-03-10T14:00:00Z", archivedAt: "2024-03-14T14:00:00Z", accuracyPct: 94 },
      { name: "Northgate–Halstead 2024", capturedAt: "2024-03-14T14:00:00Z", archivedAt: null, accuracyPct: 96 },
      { name: "Northgate–Halstead 2026", capturedAt: "2026-03-12T14:00:00Z", archivedAt: null, accuracyPct: 98 },
    ],
  },
  // Missed the latest flights - S08's own history table shows exactly this.
  {
    name: "Dunmore",
    utilityName: UTILITY,
    description: null,
    corridors: ["Dunmore 14"],
    historicalCadenceYears: 4,
    rounds: [
      { name: "Dunmore 2022", capturedAt: "2022-03-10T14:00:00Z", archivedAt: "2024-03-14T14:00:00Z", accuracyPct: 94 },
      { name: "Dunmore 2024", capturedAt: "2024-03-14T14:00:00Z", archivedAt: null, accuracyPct: 96 },
    ],
  },
  {
    name: "West Bay",
    utilityName: UTILITY,
    description: null,
    corridors: ["West Bay 09", "Ames Hollow 27"],
    historicalCadenceYears: 5,
    rounds: [{ name: "West Bay", capturedAt: "2024-03-14T14:00:00Z", archivedAt: "2026-03-12T14:00:00Z", accuracyPct: 96 }],
  },
  {
    name: "West Bay 2.0",
    utilityName: UTILITY,
    description: null,
    corridors: ["West Bay 09", "Ames Hollow 27"],
    historicalCadenceYears: 5,
    rounds: [{ name: "West Bay 2.0", capturedAt: "2026-03-12T14:00:00Z", archivedAt: null, accuracyPct: 98 }],
  },
];

function corridorsFlown(project: ProjectDef, round: RoundDef): string[] {
  return round.corridors ?? project.corridors;
}

/* "Today" inside the corpus's own fictional timeline - the wall clock this
 * session runs on is years ahead of every date the deck and this corpus use,
 * so staleness ("11 months old", "over 9 months") is computed against this
 * instead of Date.now(). */
export const MOCK_TODAY = "2026-03-14T21:00:00Z";

// ---------------------------------------------------------------------------
// 2. Build projects, rounds, captures, poles, spans, trees
// ---------------------------------------------------------------------------

/* Empty, and that is the realistic state: circuits come from the utility's
 * feeder GIS (11 R8), and every circuit_id below is null (15 section 6.1).
 * api/assessments.ts reads this - the engine prices per circuit (16), so its
 * per-circuit arrays are empty until circuits exist. */
export const circuits: Circuit[] = [];

export const projects: Project[] = [];

/* A project's span figures where nothing is measured (15 section 3.4a). The
 * figures are the server's, and no screen reading a mock project shows them,
 * so a mock project carries this rather than a second computation of them. */
export const NOTHING_MEASURED: Pick<Project, "span_verdict_counts" | "measured_length_m" | "mean_clearance_m"> = {
  span_verdict_counts: { Critical: 0, High: 0, Medium: 0, Acceptable: 0, Unmeasured: 0 },
  measured_length_m: null,
  mean_clearance_m: null,
};

/* GET /api/projects/{id}/rounds, keyed by project id, ordinal ascending. */
export const roundsByProject = new Map<string, CaptureRound[]>();
export const captures: Capture[] = [];
export const poles: Pole[] = [];
export const spans: Span[] = [];
export const trees: Tree[] = [];
/* The spans each latest-round capture measured, by capture id. */
const spanIdsByCapture = new Map<string, string[]>();

/* The single source for "where Acceptable starts at this voltage
 * class" - S10's ANSI default alongside the in-use figure the deck shows
 * overridden for three classes. Everything that needs a threshold (the
 * risk-step helpers below, the S10 screen itself) reads
 * in_use_m from clearanceThresholds below rather than a second hard-coded
 * table. */
const ANSI_A300_FT: Record<string, number> = { "4 kV": 6.0, "12 kV": 8.0, "25 kV": 10.0, "69 kV": 15.0, "138 kV": 20.0 };
const IN_USE_FT: Record<string, number> = { "4 kV": 6.0, "12 kV": 10.0, "25 kV": 12.0, "69 kV": 15.0, "138 kV": 25.0 };

/* Kanopy's horizontal zones per class, in feet - where red, orange and yellow
 * end - as the corpus project's clearance_standards carries them (05 section
 * 5). Each voltage class above falls in the range Kanopy names for one of
 * these. */
const KANOPY_ZONES_FT = {
  Distribution: { red: 2, orange: 4, yellow: 10 }, // 4-13.2 kV
  SubTransmission: { red: 1, orange: 4, yellow: 12 }, // 23-69 kV
  Transmission: { red: 4, orange: 8, yellow: 18 }, // 115-345 kV
};
const KANOPY_CLASS: Record<string, keyof typeof KANOPY_ZONES_FT> = {
  "4 kV": "Distribution",
  "12 kV": "Distribution",
  "25 kV": "SubTransmission",
  "69 kV": "SubTransmission",
  "138 kV": "Transmission",
};

function bandShape(voltageClass: string): BandShape {
  const zones = KANOPY_ZONES_FT[KANOPY_CLASS[voltageClass]];
  return { critical_fraction: zones.red / zones.yellow, high_fraction: zones.orange / zones.yellow };
}

function clearanceThresholdFor(voltageClass: string | null | undefined): ClearanceThreshold {
  const fallback = clearanceThresholds.find((t) => t.voltage_class === "12 kV")!;
  if (!voltageClass) return fallback;
  return clearanceThresholds.find((t) => t.voltage_class === voltageClass) ?? fallback;
}

export function thresholdForVoltageClass(voltageClass: string | null | undefined): number {
  return clearanceThresholdFor(voltageClass).in_use_m;
}

/* A tree or span is scored at its capture's class (02 -> A capture's class
 * is supplied at upload). These stand in for the risk_step the API serves (15
 * sections 3.2, 3.3). A tree goes through its capture because its own
 * voltage_class is the analysis's reading, null wherever the analysis is. */
export function thresholdForSpan(span: Span): number {
  return thresholdForVoltageClass(span.voltage_class);
}

function voltageClassOfTree(tree: Tree): string | null | undefined {
  return captures.find((c) => c.id === tree.capture_ids[0])?.voltage_class;
}

export function thresholdForTree(tree: Tree): number {
  return thresholdForVoltageClass(voltageClassOfTree(tree));
}

/* null where none of the span's trees reports a distance (15 section 3.3). */
export function riskStepForSpan(span: Span): RiskStep | null {
  if (span.min_clearance_m === null) return null;
  const t = clearanceThresholdFor(span.voltage_class);
  return riskStepForClearance(span.min_clearance_m, t.in_use_m, t);
}

const SPECIES = [
  "Northern red oak",
  "Red maple",
  "Black cherry",
  "White ash",
  "Silver maple",
  "Black locust",
  "Sugar maple",
  "Eastern cottonwood",
];

let treeSeq = 0;
let spanSeq = 0;
let poleSeq = 0;

function roundId(projectIndex: number, roundIndex: number): string {
  return uuid("round", projectIndex * 10 + roundIndex);
}

// Deterministic, so a tree can name its capture before the captures are
// built below.
function captureId(projectIndex: number, roundIndex: number, corridorIndex: number): string {
  return uuid("capture", projectIndex * 1000 + roundIndex * 20 + corridorIndex);
}

/* Each corridor's pole walk is drawn once, so projects flying the same
 * corridor - the West Bay pair - put their poles in the same places. */
const poleWalks = new Map<string, Coordinates[]>();

function poleWalk(corridorIndex: number): Coordinates[] {
  const name = CORRIDOR_DEFS[corridorIndex].name;
  const cached = poleWalks.get(name);
  if (cached) return cached;
  // One more pole than spans, walking a slightly wandering line from the
  // corridor's origin so the mock map shows a corridor, not a straight ruler.
  const walk: Coordinates[] = [];
  let { lat, lon } = ORIGINS[corridorIndex];
  let heading = rng.float(0, Math.PI * 2);
  for (let p = 0; p <= CORRIDOR_DEFS[corridorIndex].spanCount; p++) {
    heading += rng.float(-0.15, 0.15);
    lat += Math.cos(heading) * 0.0009;
    lon += Math.sin(heading) * 0.0009;
    walk.push({ lat, lon });
  }
  poleWalks.set(name, walk);
  return walk;
}

for (let pi = 0; pi < PROJECT_DEFS.length; pi++) {
  const def = PROJECT_DEFS[pi];
  const projectId = uuid("project", pi);
  const latestIndex = def.rounds.length - 1;
  const latestRound = def.rounds[latestIndex];
  const latestRoundId = roundId(pi, latestIndex);

  // Only the latest round is materialised row by row - every screen reads a
  // project's latest round (15 section 3.4a, "as of latest_round_id") - so
  // earlier rounds carry plausible counts on their captures, and reach the
  // trend charts only as points in each tree's history.
  const corridorTreeIds = new Map<string, string[]>();
  const corridorSpanIds = new Map<string, string[]>();
  const corridorPoleIds = new Map<string, string[]>();

  // Position along the project, corridor after corridor - the sort the
  // API orders spans by (15 section 3.3).
  let cumulativeLengthM = 0;

  for (const corridorName of corridorsFlown(def, latestRound)) {
    const ci = CORRIDOR_DEFS.findIndex((c) => c.name === corridorName);
    const corridor = CORRIDOR_DEFS[ci];
    const thresholdM = feetToMeters(IN_USE_FT[corridor.voltageClass]);
    const shape = bandShape(corridor.voltageClass);

    const treeIds: string[] = [];
    const spanIds: string[] = [];
    const poleIds: string[] = [];

    // Kept as a local array (not re-derived from the global poles[] by
    // index) so span generation below can't drift onto another corridor's
    // poles.
    const poleObjs: Pole[] = poleWalk(ci).map((location, p) => {
      const pole: Pole = {
        id: uuid("pole", poleSeq++),
        kanopy_pole_id: "",
        location,
        height_m: rng.float(9, 15),
        label_name: `P-${4000 + ci * 500 + p}`,
        label: rng.bool(0.7) ? `P-${4000 + ci * 500 + p}` : null,
        circuit_id: null,
      };
      pole.kanopy_pole_id = `kp-${pole.id}`;
      poles.push(pole);
      poleIds.push(pole.id);
      return pole;
    });

    for (let s = 0; s < corridor.spanCount; s++) {
      const fromPole = poleObjs[s];
      const toPole = poleObjs[s + 1];
      const fromLoc = fromPole.location;
      const toLoc = toPole.location;
      const lengthM = rng.float(55, 110);
      cumulativeLengthM += lengthM;

      const spanId = uuid("span", spanSeq++);
      spanIds.push(spanId);

      const treeCount = rng.int(1, 6);
      const spanTreeMinClearances: number[] = [];
      const spanReportedM: number[] = [];
      let spanP0 = 0;
      let spanP1 = 0;

      for (let t = 0; t < treeCount; t++) {
        const treeId = uuid("tree", treeSeq++);
        treeIds.push(treeId);

        const midLat = (fromLoc.lat + toLoc.lat) / 2 + rng.float(-0.0002, 0.0002);
        const midLon = (fromLoc.lon + toLoc.lon) / 2 + rng.float(-0.0002, 0.0002);

        // Clearance skews toward "fine" - most of a real corridor is not at
        // risk (07-domain-mapping-spec.md), with a long tail down toward zero.
        // The risk step is taken at the span's worst tree, so these odds are
        // tuned lower than the per-tree target - a span with several trees
        // otherwise reads as High or Critical far more often than any one
        // tree does. The tail reaches below a foot because the 4 kV and
        // 25 kV classes turn Critical only around there.
        const clearanceRoll = rng.next();
        const minAbsDistM = feetToMeters(
          clearanceRoll < 0.04
            ? rng.float(0.3, 6)
            : clearanceRoll < 0.14
              ? rng.float(6, 10)
              : rng.float(10, 22),
        );
        spanTreeMinClearances.push(minAbsDistM);

        const riskStep = riskStepForClearance(minAbsDistM, thresholdM, shape);

        const hullVolumeM3 = rng.float(0.4, 38);
        const volumeBand: VolumeBand = hullVolumeM3 < 3 ? "s" : hullVolumeM3 < 10 ? "m" : hullVolumeM3 < 22 ? "l" : "xl";

        // 07-domain-mapping-spec.md: the majority of a capture is deliberately
        // unassessed - Kanopy scopes analysis to trees near the conductor.
        const assessed = rng.bool(0.35);
        let assessedAt: string | null = null;
        let isEncroaching: boolean | null = null;
        let conditionBand: ConditionBand | null = null;
        let prescription: Prescription | null = null;
        let pruningVolumeM3: number | null = null;
        let pruningFraction: number | null = null;
        let prescriptionWhat: string | null = null;
        let prescriptionHow: string | null = null;

        if (assessed) {
          assessedAt = latestRound.capturedAt;
          isEncroaching = riskStep === "High" || riskStep === "Critical";
          conditionBand = rng.bool(0.55)
            ? "Acceptable"
            : rng.bool(0.6)
              ? "Medium"
              : rng.bool(0.7)
                ? "High"
                : "Critical";
          // Assessed does not mean "needs work" - 15 section 1.4's trap is
          // exactly this: most assessed trees still resolve to "none".
          prescription = rng.bool(0.7) ? "none" : rng.bool(0.75) ? "prune" : "remove";
          if (prescription !== "none") {
            pruningVolumeM3 = +(hullVolumeM3 * rng.float(0.1, 0.4)).toFixed(2);
            pruningFraction = +(pruningVolumeM3 / hullVolumeM3).toFixed(2);
            prescriptionWhat = prescription === "remove" ? "Removal" : "Directional prune";
            prescriptionHow = prescription === "remove" ? "Full removal, stump left" : `Prune to ${(thresholdM * 1.2).toFixed(1)} m clearance`;
          } else {
            prescriptionWhat = "None";
            prescriptionHow = "No action required";
          }
        }

        // Analysis block: independent of the assessment gate (15 section 3.2 -
        // "additionally null where the analysis block is absent"). ~85% carry
        // an analysis result at all.
        const hasAnalysis = rng.bool(0.85);
        // "Most trees have our volume and no measured growth rate"
        // (15 section 3.2) - modelled as a straight 75/25 split
        // of the trees that do have analysis.
        const growthMeasured = hasAnalysis && rng.bool(0.25);
        const speciesMeanCmPerYear = 65; // capture median stand-in, all species
        const growthRateCmPerYear = hasAnalysis
          ? growthMeasured
            ? rng.float(40, 95)
            : speciesMeanCmPerYear + rng.float(-8, 8)
          : null;

        // 15-api-contract.md section 3.2: null on 3% - a data-completeness
        // gap independent of whether the tree was assessed, so this roll is
        // separate from `assessed` above. A distance the tree does not report
        // reaches nothing the server builds from it: not its proximity flags,
        // and not its span's clearance (15 section 3.3). The rest of this
        // tree's figures still come from minAbsDistM.
        const distanceM = rng.bool(0.97) ? minAbsDistM : null;
        const p0 = distanceM !== null && riskStep === "Critical";
        const p1 = distanceM !== null && (riskStep === "High" || riskStep === "Critical");
        if (p0) spanP0++;
        if (p1) spanP1++;
        if (distanceM !== null) spanReportedM.push(distanceM);

        const tree: Tree = {
          id: treeId,
          kanopy_tree_id: `kt-${treeId}`,
          circuit_id: null,
          span_id: spanId,
          capture_ids: [captureId(pi, latestIndex, ci)],
          capture_round_id: latestRoundId,
          project_id: projectId,
          location: { lat: midLat, lon: midLon },
          altitude_m: rng.float(2, 30),

          min_absolute_distance_m: distanceM === null ? null : +distanceM.toFixed(2),
          min_lateral_clearance_m: +(minAbsDistM * rng.float(0.9, 1.05)).toFixed(2),
          encroachment_height_m: +rng.float(0, 3).toFixed(2),
          min_overhang_height_m: rng.bool(0.4) ? +rng.float(0, 5).toFixed(2) : null,
          min_underhang_height_m: rng.bool(0.3) ? +rng.float(0, 4).toFixed(2) : null,

          hull_volume_m3: +hullVolumeM3.toFixed(2),
          volume_band: volumeBand,
          p0,
          p1,

          assessed_at: assessedAt,
          is_encroaching: isEncroaching,
          condition_band: conditionBand,
          prescription,
          prescription_reason: prescription && prescription !== "none"
            ? { deficit_m: +(thresholdM - minAbsDistM).toFixed(2), condition_band: conditionBand }
            : prescription === "none"
              ? { note: "within threshold and condition" }
              : null,
          pruning_volume_m3: pruningVolumeM3,
          pruning_fraction: pruningFraction,

          species_primary: hasAnalysis ? rng.pick(SPECIES) : null,
          species_confidence: hasAnalysis ? rng.pick(["low", "medium", "high"] as const) : null,
          growth_rate_class: hasAnalysis ? (growthRateCmPerYear! > 75 ? "Fast" : growthRateCmPerYear! > 55 ? "Medium" : "Slow") : null,
          growth_rate_cm_per_year: growthRateCmPerYear === null ? null : +growthRateCmPerYear.toFixed(1),
          growth_rate_inferred: hasAnalysis ? !growthMeasured : false,
          health_grade: hasAnalysis ? rng.pick(["A", "B", "C", "D"] as const) : null,
          foliage_state: hasAnalysis ? rng.pick(["full", "partial", "senescent"]) : null,
          months_to_clearance_breach: hasAnalysis && growthRateCmPerYear
            ? Math.max(0, Math.round(((minAbsDistM - thresholdM) / (growthRateCmPerYear / 100)) * 12))
            : null,
          voltage_class: hasAnalysis ? corridor.voltageClass : null,
          crew_safety: hasAnalysis && rng.bool(0.2) ? "Overhead primary within reach radius; bucket truck access from shoulder." : null,
          prescription_what: prescriptionWhat,
          prescription_how: prescriptionHow,

          frame_count: rng.int(1, 4),
        };

        trees.push(tree);
      }

      // treeCount is rng.int(1, 6), so spanTreeMinClearances always has at
      // least one entry - the min/mean below never fall through to a default.
      const minClearanceM = Math.min(...spanTreeMinClearances);
      const meanClearanceM = spanTreeMinClearances.reduce((a, b) => a + b, 0) / spanTreeMinClearances.length;
      // Over the distances the trees report, so a span whose trees report
      // none has no clearance at all (15 section 3.3).
      const reportedMinM = spanReportedM.length > 0 ? Math.min(...spanReportedM) : null;
      spans.push({
        id: spanId,
        from_pole_id: fromPole.id,
        to_pole_id: toPole.id,
        from_pole_label: fromPole.label,
        to_pole_label: toPole.label,
        circuit_id: null,
        capture_round_id: latestRoundId,
        project_id: projectId,
        geometry: { type: "LineString", coordinates: [[fromLoc.lon, fromLoc.lat], [toLoc.lon, toLoc.lat]] },
        midpoint: { lat: (fromLoc.lat + toLoc.lat) / 2, lon: (fromLoc.lon + toLoc.lon) / 2 },
        length_m: +lengthM.toFixed(1),
        covered_length_m: +lengthM.toFixed(1),
        cumulative_length_m: +cumulativeLengthM.toFixed(1),
        vegetated_distance_min_m: +minClearanceM.toFixed(2),
        vegetated_distance_mean_m: +meanClearanceM.toFixed(2),
        voltage_class: corridor.voltageClass,
        tree_count: treeCount,
        p0_count: spanP0,
        p1_count: spanP1,
        capture_accuracy_pct: latestRound.accuracyPct,
        min_clearance_m: reportedMinM === null ? null : +reportedMinM.toFixed(2),
      });
    }

    corridorTreeIds.set(corridorName, treeIds);
    corridorSpanIds.set(corridorName, spanIds);
    corridorPoleIds.set(corridorName, poleIds);
    spanIdsByCapture.set(captureId(pi, latestIndex, ci), spanIds);
  }

  // Captures: one per (round, corridor) the round flew. The latest round's
  // counts are the rows generated above; earlier rounds carry plausible
  // counts, since their rows are not materialised.
  const rounds: CaptureRound[] = [];
  let latestCaptures: Capture[] = [];
  for (let ri = 0; ri < def.rounds.length; ri++) {
    const round = def.rounds[ri];
    const isLatest = ri === latestIndex;
    const roundCaptures: Capture[] = [];

    for (const corridorName of corridorsFlown(def, round)) {
      const ci = CORRIDOR_DEFS.findIndex((c) => c.name === corridorName);
      const corridor = CORRIDOR_DEFS[ci];
      const latestTreeCount = corridorTreeIds.get(corridorName)?.length ?? 0;
      const id = captureId(pi, ri, ci);
      roundCaptures.push({
        id,
        kanopy_job_id: `job-${id}`,
        captured_at: round.capturedAt,
        corridor_length_m: corridor.lengthMi * 1609.344,
        flight_location_title: `${corridorName} corridor`,
        metric_accuracy_m: 0.05,
        metric_accuracy_pct: round.accuracyPct,
        voltage_class: corridor.voltageClass,
        conductor_class: "ACSR",
        circuit_clearance_m: feetToMeters(IN_USE_FT[corridor.voltageClass]),
        circuit_width_m: 30,
        phase_count: 3,
        circuit_count: 1,
        capture_device: "DJI M300 + Kanopy payload",
        status: round.archivedAt ? "archived" : "published",
        published_at: round.capturedAt,
        tree_count: isLatest ? latestTreeCount : Math.round(latestTreeCount * rng.float(0.85, 1.1)),
        pole_count: isLatest ? (corridorPoleIds.get(corridorName)?.length ?? 0) : corridor.spanCount + 1,
        span_count: isLatest ? (corridorSpanIds.get(corridorName)?.length ?? 0) : corridor.spanCount,
        circuit_ids: [],
        progress_pct: null,
        progress_stage: null,
        eta_minutes: null,
      });
    }

    if (isLatest) {
      for (const [k, flight] of (def.inFlight ?? []).entries()) {
        const ci = CORRIDOR_DEFS.findIndex((c) => c.name === flight.corridor);
        const corridor = CORRIDOR_DEFS[ci];
        const latestTreeCount = corridorTreeIds.get(flight.corridor)?.length ?? 0;
        const id = uuid("capture", pi * 1000 + 900 + k);
        roundCaptures.push({
          id,
          kanopy_job_id: `job-${id}`,
          captured_at: flight.capturedAt,
          corridor_length_m: corridor.lengthMi * 1609.344,
          flight_location_title: `${flight.corridor} corridor`,
          metric_accuracy_m: 0.05,
          metric_accuracy_pct: 0,
          voltage_class: corridor.voltageClass,
          conductor_class: "ACSR",
          circuit_clearance_m: feetToMeters(IN_USE_FT[corridor.voltageClass]),
          circuit_width_m: 30,
          phase_count: 3,
          circuit_count: 1,
          capture_device: "DJI M300 + Kanopy payload",
          status: flight.status,
          published_at: null,
          tree_count: Math.round(latestTreeCount * rng.float(0.85, 1.1)),
          pole_count: corridor.spanCount + 1,
          span_count: corridor.spanCount,
          circuit_ids: [],
          progress_pct: flight.progressPct ?? null,
          progress_stage: flight.progressStage ?? null,
          eta_minutes: flight.etaMinutes ?? null,
        });
      }
    }

    captures.push(...roundCaptures);
    if (isLatest) latestCaptures = roundCaptures;

    // A round's figures are its published captures' - a capture still
    // processing, or one that failed, has measured nothing yet.
    const counted = roundCaptures.filter((c) => c.published_at !== null);
    const roundTreeCount = counted.reduce((sum, c) => sum + c.tree_count, 0);
    const latestRoundTrees = isLatest ? trees.filter((t) => t.capture_round_id === latestRoundId) : [];
    const capturedTimes = counted.map((c) => c.captured_at).filter((at): at is string => at !== null).sort();
    rounds.push({
      id: roundId(pi, ri),
      ordinal: ri + 1,
      name: round.name,
      archived_at: round.archivedAt,
      capture_count: roundCaptures.length,
      tree_count: roundTreeCount,
      assessed_tree_count: isLatest
        ? latestRoundTrees.filter((t) => t.assessed_at !== null).length
        : Math.round(roundTreeCount * rng.float(0.3, 0.4)),
      latest_captured_at: capturedTimes[capturedTimes.length - 1] ?? null,
    });
  }

  roundsByProject.set(projectId, rounds);
  const latest = rounds[rounds.length - 1];
  projects.push({
    id: projectId,
    name: def.name,
    utility_name: def.utilityName,
    description: def.description,
    closed_at: null,
    round_count: rounds.length,
    latest_round_id: latest.id,
    latest_captured_at: latest.latest_captured_at,
    circuit_count: 0,
    tree_count: latest.tree_count,
    span_count: spans.filter((s) => s.capture_round_id === latest.id).length,
    // Every capture of the round, as the server sums them (15 section 3.4a).
    corridor_length_m: latestCaptures.reduce((sum, c) => sum + c.corridor_length_m, 0),
    ...NOTHING_MEASURED,
  });
}

/* A corridor's most recent flight - what a work packet or a report draft
 * points at. A corridor flown by more than one project resolves to the one
 * listed last, which is the current one of the West Bay pair. */
function latestFlightOf(corridorName: string): { projectId: string; captureId: string } {
  const ci = CORRIDOR_DEFS.findIndex((c) => c.name === corridorName);
  let pi = PROJECT_DEFS.length - 1;
  while (!PROJECT_DEFS[pi].corridors.includes(corridorName)) pi--;
  return { projectId: uuid("project", pi), captureId: captureId(pi, PROJECT_DEFS[pi].rounds.length - 1, ci) };
}

// ---------------------------------------------------------------------------
// 4. Synthetic-only resources - no contract or schema source (05 section 8)
// ---------------------------------------------------------------------------

export const clearanceThresholds: ClearanceThreshold[] = Object.keys(ANSI_A300_FT).map((voltageClass) => ({
  voltage_class: voltageClass,
  ansi_a300_m: feetToMeters(ANSI_A300_FT[voltageClass]),
  in_use_m: feetToMeters(IN_USE_FT[voltageClass]),
  source: ANSI_A300_FT[voltageClass] === IN_USE_FT[voltageClass] ? "Default" : "Override",
  ...bandShape(voltageClass),
}));

export const notifications: AppNotification[] = [
  { id: uuid("notif", 0), category: "capture", headline: "Capture failed — Ridgeline 42, north segment", detail: "Sensor dropout across 41 frames. Re-run scheduled for 14 Mar 2026, 06:00. No action required.", occurred_at: "2026-03-14T08:14:00Z", read: false },
  { id: uuid("notif", 1), category: "risk_change", headline: "318 spans moved to Critical on the 12 Mar capture", detail: "Concentrated on Ridgeline 42 and Marsh Creek 17. Review the work plan.", occurred_at: "2026-03-14T07:02:00Z", read: false },
  { id: uuid("notif", 2), category: "capture", headline: "Capture 12 Mar 2026 published", detail: "12,400 spans measured at 98% coverage. Confidence high.", occurred_at: "2026-03-14T06:40:00Z", read: false },
  { id: uuid("notif", 3), category: "plan", headline: "Work plan FY2026 Q2–Q4 approved by D. Reyes", detail: "1,307 spans, $4.28M, six packages. Exported to work orders.", occurred_at: "2026-03-13T16:22:00Z", read: true },
  { id: uuid("notif", 4), category: "capture", headline: "Marsh Creek 17 measurements are 11 months old", detail: "Confidence reduced to medium. Schedule a capture to restore high confidence.", occurred_at: "2026-03-13T09:05:00Z", read: true },
  { id: uuid("notif", 5), category: "threshold", headline: "Threshold override applied to 12 kV class", detail: "Changed 8.0 ft to 10.0 ft by J. Marsh. 1,842 spans rescored.", occurred_at: "2026-03-13T08:31:00Z", read: true },
];

export const cadenceScenarios: CadenceScenario[] = [
  { cycle_years: 3, pct_clear: 0.99, annual_cost: { cents: 5_700_000_00, currency: "USD" }, spans_below_by_year: [40, 90, 140, 190, 230] },
  { cycle_years: 4, pct_clear: 0.96, annual_cost: { cents: 4_300_000_00, currency: "USD" }, spans_below_by_year: [40, 90, 150, 260, 340] },
  { cycle_years: 5, pct_clear: 0.81, annual_cost: { cents: 3_900_000_00, currency: "USD" }, spans_below_by_year: [40, 100, 220, 420, 640] },
];

// A packet is cut from one corridor's latest flight, and belongs to the project
// that flew it.
const PACKET_DEFS: { code: string; corridor: string; label: string; fy: number; driver: WorkPacket["driver"] }[] = [
  { code: "PK-01", corridor: "Ridgeline 42", label: "Ridgeline 42 — north", fy: 2026, driver: "critical" },
  { code: "PK-02", corridor: "Marsh Creek 17", label: "Marsh Creek 17 — east", fy: 2026, driver: "critical" },
  { code: "PK-03", corridor: "Fielding 08", label: "Fielding 08 — full", fy: 2026, driver: "high" },
  { code: "PK-04", corridor: "Northgate 23", label: "Northgate 23 — south", fy: 2026, driver: "high" },
  { code: "PK-05", corridor: "Halstead 05", label: "Halstead 05 — full", fy: 2026, driver: "cycle" },
  { code: "PK-06", corridor: "Ridgeline 42", label: "Distributed Critical remainder", fy: 2026, driver: "deferred_eligible" },
  { code: "PK-11", corridor: "Corley 31", label: "Corley 31 — north", fy: 2027, driver: "high" },
  { code: "PK-14", corridor: "Dunmore 14", label: "Dunmore 14 — full", fy: 2027, driver: "cycle" },
  { code: "PK-19", corridor: "Ridgeline 42", label: "Ridgeline 42 — mid", fy: 2028, driver: "cycle" },
  { code: "PK-26", corridor: "West Bay 09", label: "West Bay 09 — full", fy: 2029, driver: "cycle" },
  { code: "PK-33", corridor: "Ames Hollow 27", label: "Ames Hollow 27 — full", fy: 2030, driver: "cycle" },
];

export const workPackets: WorkPacket[] = PACKET_DEFS.map((def, i) => {
  const spanCount = rng.int(80, 320);
  const treeCount = Math.round(spanCount * rng.float(1.1, 1.4));
  const crewHours = Math.round(spanCount * rng.float(6, 9));
  const costCents = Math.round(spanCount * 435 * 100);
  return {
    id: uuid("packet", i),
    code: def.code,
    project_id: latestFlightOf(def.corridor).projectId,
    label: def.label,
    fiscal_year: def.fy,
    driver: def.driver,
    span_count: spanCount,
    tree_count: treeCount,
    removal_count: Math.round(treeCount * rng.float(0.1, 0.25)),
    crew_hours: crewHours,
    cost: { cents: costCents, currency: "USD" },
    window_start: `${def.fy}-04-06`,
    window_end: `${def.fy}-05-29`,
    status: def.fy === 2026 ? "approved" : "draft",
    outage_count: rng.int(0, 3),
    assigned_crews: [`Crew ${rng.int(1, 6)}`, `Crew ${rng.int(1, 6)}`],
    blockers: rng.bool(0.3) ? ["1 landowner refusal on file"] : [],
  };
});

export const workPacketLineItems: WorkPacketLineItem[] = workPackets.flatMap((packet, p) => {
  const corridorSpans = (spanIdsByCapture.get(latestFlightOf(PACKET_DEFS[p].corridor).captureId) ?? [])
    .slice(0, 6)
    .map((id) => spans.find((s) => s.id === id)!);
  return corridorSpans.map((span, i) => {
    const spanTrees = trees.filter((t) => t.span_id === span.id);
    const tree = spanTrees[0];
    return {
      packet_id: packet.id,
      span_id: span.id,
      tree_id: tree?.id ?? uuid("tree-synthetic", i),
      tree_label: tree ? `${treeHandle(tree.id)} ${tree.species_primary ?? "Unidentified"}` : `T-synthetic-${i}`,
      prescription_label: tree?.prescription === "remove" ? "Removal" : "Directional prune",
      hours: +rng.float(0.8, 3.2).toFixed(1),
      access_status: rng.pick(["clear", "clear", "clear", "refusal_on_file", "permit_pending", "outage_required"] as const),
    };
  });
});

export const workPlanPortfolio: WorkPlanPortfolio = {
  id: uuid("portfolio", 0),
  horizon_start_fy: 2026,
  horizon_end_fy: 2030,
  scenario_name: "Recommended",
  span_count: workPackets.reduce((sum, p) => sum + p.span_count, 0),
  five_year_cost: { cents: workPackets.reduce((sum, p) => sum + p.cost.cents, 0), currency: "USD" },
  annual_mean_cost: { cents: Math.round(workPackets.reduce((sum, p) => sum + p.cost.cents, 0) / 5), currency: "USD" },
  packet_count: workPackets.length,
  crew_years: +(workPackets.reduce((sum, p) => sum + p.crew_hours, 0) / 2080).toFixed(1),
  budget_used_pct: 0.95,
  budget_remaining: { cents: 220_000_00, currency: "USD" },
  backlog_at_horizon_end: 412,
  // Synthetic: one project short of its cadence by the horizon, so the tile
  // reads as a gap rather than a formality.
  projects_reaching_cadence: projects.length - 1,
  projects_total: projects.length,
  unfunded_critical_current_fy: 0,
  crew_shortfall_hours: 180,
};

export const reportDrafts: ReportDraft[] = [
  {
    id: uuid("report", 0),
    title: "System clearance assessment",
    status: "draft",
    edited_at: new Date(Date.now() - 2 * 60_000).toISOString(),
    scope: "all",
    capture_id: latestFlightOf("Ridgeline 42").captureId,
    format: "pdf",
    sections: [
      { key: "summary", label: "Summary of findings", required: false, included: true },
      { key: "method", label: "Method and coverage", required: true, included: true },
      { key: "by_project", label: "Findings by project", required: false, included: true },
      { key: "work_plan", label: "Recommended work plan", required: false, included: true },
      { key: "cadence", label: "Recommended cycle cadence", required: false, included: true },
      { key: "span_appendix", label: "Span-level appendix", required: false, included: false },
      { key: "imagery_appendix", label: "Capture imagery appendix", required: false, included: false },
    ],
  },
];
